import { env } from '@/config/env';

/**
 * The HTTP client — the ONLY point of contact with the backend.
 *
 * Warning: No component and no hook calls `fetch` directly: everything goes through
 * here, then through `@repo/api-client`. That is what lets the transport change
 * without the UI moving.
 */

/** The exact JSON error body returned by the backend's exception filter. */
export interface ApiErrorBody {
  statusCode: number;
  errorCode: string;
  message: string | string[];
}

/**
 * An error answered by the API, carrying its stable `errorCode`.
 *
 * Warning: Those codes **are** i18n keys of the `errors` namespace. Never display
 * `message` to the reader — it is English, server-side.
 *
 * @see docs/features/i18n-errors.md
 */
export class ApiError extends Error {
  readonly statusCode: number;
  readonly errorCode: string;

  constructor(body: ApiErrorBody) {
    super(Array.isArray(body.message) ? body.message.join(' ') : body.message);
    this.name = 'ApiError';
    this.statusCode = body.statusCode;
    this.errorCode = body.errorCode;
  }
}

/** Version du contrat d'API que ce client sait consommer. */
const API_VERSION = '1';

const NETWORK_ERROR: ApiErrorBody = {
  statusCode: 0,
  errorCode: 'common.error.network',
  message: 'Le serveur est injoignable.',
};

/* -------------------------------------------------------------------------
 * Token injection.
 *
 * Warning: `lib/` does not know `features/auth` — dependencies point features → lib,
 * never the other way. The auth feature registers itself at start-up through
 * `configureHttpAuth`, which keeps this module testable in isolation.
 * ---------------------------------------------------------------------- */
type AuthHooks = {
  getAccessToken: () => string | null;
  /**
   * Asks the session to renew the token after a 401.
   *
   * ⚠️ Reçoit le chemin appelé : c'est ce qui permet à la session de refuser
   * de renouveler pour son PROPRE point d'entrée. Sans ce paramètre, le 401
   * du rafraîchissement redemandait un rafraîchissement, qui attendait celui
   * qui l'avait déclenché — l'app restait sur « Chargement… » pour de bon.
   *
   * @param path The path whose call answered 401.
   * @returns `true` when a fresh token was obtained and the call may be replayed.
   */
  onUnauthorized: (path: string) => Promise<boolean>;
};

let auth: AuthHooks = {
  getAccessToken: () => null,
  onUnauthorized: () => Promise.resolve(false),
};

export function configureHttpAuth(hooks: AuthHooks): void {
  auth = hooks;
}

/* ---------------------------------------------------------------------- */

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Internal: stops an infinite replay loop after a refresh. */
  _retried?: boolean;
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, headers, _retried, ...rest } = options;
  const token = auth.getAccessToken();

  // A file upload goes as `multipart/form-data`: the body is sent as-is and,
  // above all, `Content-Type` is NOT set — only the browser can add the
  // boundary delimiting the parts. Setting it by hand makes the body
  // unreadable server-side.
  const isMultipart = body instanceof FormData;

  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}${path}`, {
      ...rest,
      headers: {
        // The API is versioned by header, not by URL prefix. Sending it
        // explicitly makes the client declare which contract version it speaks,
        // rather than silently following a future change of server default.
        'X-API-Version': API_VERSION,
        ...(body !== undefined &&
          !isMultipart && { 'Content-Type': 'application/json' }),
        ...(token && { Authorization: `Bearer ${token}` }),
        ...headers,
      },
      ...(body !== undefined && {
        body: isMultipart ? body : JSON.stringify(body),
      }),
    });
  } catch {
    throw new ApiError(NETWORK_ERROR);
  }

  // 401 → une tentative de rafraîchissement, puis un seul rejeu.
  if (response.status === 401 && !_retried) {
    if (await auth.onUnauthorized(path)) {
      return request<T>(path, { ...options, _retried: true });
    }
  }

  if (!response.ok) {
    const parsed: unknown = await response.json().catch(() => null);
    throw new ApiError(
      isApiErrorBody(parsed)
        ? parsed
        : {
            statusCode: response.status,
            errorCode: 'common.error.unexpected',
            message: response.statusText,
          },
    );
  }

  // ⚠️ Un corps vide n'arrive pas qu'en 204. Une route qui ne renvoie rien
  // répond 201 sur un POST (Nest ne met 204 que si on le lui demande), et
  // `response.json()` lève alors sur une chaîne vide — la requête a réussi,
  // le client la voit échouer, et l'écran ne se rafraîchit pas. Vu sur
  // `POST /wishlists/:id/convert-library` : le jeu entrait bien en
  // bibliothèque et la liste ne bougeait plus.
  //
  // ⚠️ Et `null`, pas `undefined` : une route peut légitimement répondre
  // « rien à proposer » (`GET /recommendations/evening` sur une étagère vide
  // renvoie `null`, donc un corps vide), et React Query refuse `undefined`
  // comme donnée — la requête réussissait et l'écran affichait une erreur.
  const text = await response.text();
  return (text === '' ? null : JSON.parse(text)) as T;
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    'errorCode' in value &&
    typeof (value as ApiErrorBody).errorCode === 'string'
  );
}

export const http = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
